/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { View } from 'react-native';
import { isWeb, fullWidth } from '@global/screens';
import Colors from '@global/colors';
import { hasNoAccess } from '@global/helpers';
import Button from '@components/form/Button';
import RadioButton from '@components/form/RadioButton';
import SelectBox from '@components/form/SelectBox';
import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import * as Layout from '@components/screen/Layout';
import StatusBadge from '@components/global/StatusBadge';
import ModalHalf from '@components/modal/ModalHalf';
import MoreButton from '@components/navbar/MoreButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import { filterAdded } from '@/store/params';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetCmsListQuery,
  useGetCmsCountQuery,
  useGetCmsInfiniteListInfiniteQuery,
} from '@api/listApi';

const PAGE_TYPE_FILTER_OPTIONS = [
  { label: 'All Page Type', value: '' },
  { label: 'Info', value: 'info' },
  { label: 'Guideline', value: 'guideline' },
];

const tableStructure = {
  title: {
    head: 'Title',
    body: 'title',
    textStyle: { fontFamily: 'figtree-medium' },
  },
  page_type: { head: 'Page Type', body: 'page_type', type: 'slot' },
  is_active: { head: 'Status', body: 'is_active', type: 'status_active' },
};

const pageTypeLabel = (type) =>
  type == 'info' ? 'Info' : type == 'guideline' ? 'Guideline' : 'Promo';

// Search-only-ish: title search (→ $regex) + a single-select page_type filter.
const toApiFilter = (raw = {}) => {
  const filter = {};
  const title = raw.title?.$regex ?? raw.title;
  if (title) {
    filter.title = { $regex: title, $options: 'i' };
  }
  if (raw.page_type) {
    filter.page_type = raw.page_type;
  }
  return filter;
};

const CMSScreen = (props) => {
  // ── selectors ──
  const dispatch = useDispatch();

  // ── local state ──
  const [showFilterModal, setShowFilterModal] = useState(false);

  // ── derived ──
  const showAddButton = hasNoAccess([
    'sales_supervisor',
    'branch_head',
    'finance_supervisor',
    'regional_manager',
    'regional_support',
    'key_account_manager',
    'group_key_account_manager',
    'national_key_account_manager',
    'distributor_manager',
    'general_manager',
    'vice_president',
    'vice_president_finance',
    'budget_controller',
    'finance_head',
    'tax_support',
    'billing_supervisor',
    'regional_distributor_manager',
    'promotor',
  ]);

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetCmsListQuery,
    countHook: useGetCmsCountQuery,
    infiniteHook: useGetCmsInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── memoized values ──
  const searchValue = useMemo(() => {
    const k = params.filter?.title;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  const pageTypeFilter = params.filter?.page_type ?? '';

  const rows = useMemo(() => {
    return isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
  }, [list.data]);

  // ── handlers ──
  const goToForm = () => {
    openChild('CMSFormScreen');
  };

  const goToDetail = ({ data }) => {
    openChild('CMSDetailScreen', {
      id: data._id,
      type: data.page_type,
      titleScreen: data.title,
    });
  };

  const onSelectPageTypeFilter = (value) => {
    setShowFilterModal(false);
    const newFilter = { ...(params.filter || {}) };
    if (value) {
      newFilter.page_type = value;
    } else {
      delete newFilter.page_type;
    }
    dispatch(filterAdded({ key: scope, filter: newFilter }));
  };

  // ── effects ──
  // Own the header so the mobile Add goes through openChild (preserves the list
  // on return); Filter opens the page-type modal.
  useEffect(() => {
    const menus = [];
    if (showAddButton) {
      menus.push({ type: 'function', name: 'Add', callback: goToForm });
    }
    menus.push({
      type: 'function',
      name: 'Filter',
      callback: () => setShowFilterModal(true),
    });
    props.navigation.setOptions({
      headerRight: () =>
        isWeb ? (
          <NotificationSettingButton {...props} />
        ) : (
          <MoreButton testID="cms-more-button" menu={menus} {...props} />
        ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.navigation, showAddButton]);

  // ── render ──
  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Title
            title="Content Management System"
            style={{ zIndex: 1000 }}>
            {showAddButton ? (
              <View style={{ flexDirection: 'row' }}>
                <Button
                  type="primary"
                  rounded={true}
                  buttonStyle={{ width: 100 }}
                  title={
                    <SociollaText
                      type="medium"
                      style={{ fontSize: 14, color: Colors.white }}>
                      + Add
                    </SociollaText>
                  }
                  onPress={() => goToForm()}
                />
              </View>
            ) : (
              false
            )}
          </Layout.Title>

          <Table
            filterName={scope}
            searchKey="title"
            searchPlaceholder="Search by title"
            structures={tableStructure}
            data={rows}
            loading={list.isLoading}
            refetching={list.isFetching}
            hasNoHide={true}
            totalData={count.data}
            skip={params.skip}
            limit={params.limit}
            hasCallback={true}
            showFilter={false}
            onClickRow={(val) => goToDetail(val)}
            searchValue={searchValue}
            moreInlineFilter={
              <SelectBox
                label="Page Type"
                hideLabel
                options={PAGE_TYPE_FILTER_OPTIONS}
                value={
                  PAGE_TYPE_FILTER_OPTIONS.find(
                    (option) => option.value === pageTypeFilter,
                  ) || PAGE_TYPE_FILTER_OPTIONS[0]
                }
                placeholder="All Page Type"
                trackBy="value"
                labelKey="label"
                isDropdown={isWeb}
                onChange={(value) => onSelectPageTypeFilter(value.value)}
              />
            }
            slot={{
              page_type: ({ data, index }) => (
                <SociollaText key={index} style={{ fontSize: 12 }}>
                  {pageTypeLabel(data.page_type)}
                </SociollaText>
              ),
            }}
          />
        </>
      ) : (
        <TableMobile
          filterName={scope}
          searchKey="title"
          placeholder="Search by title"
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          data={rows}
          totalData={count.data}
          searchValue={searchValue}
          renderItem={({ data, index }) => (
            <Layout.ItemMobile
              key={index}
              touchable={true}
              column={true}
              onPress={() => goToDetail({ data: data })}>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                }}>
                <View style={{ width: fullWidth - 140 }}>
                  <SociollaText
                    type="medium"
                    style={{ width: '100%', marginBottom: 4 }}>
                    {data.title}
                  </SociollaText>
                  <SociollaText style={{ width: '100%', color: Colors.grey1 }}>
                    {pageTypeLabel(data.page_type)}
                  </SociollaText>
                </View>
                <StatusBadge status={data.is_active ? 'active' : 'in_active'} />
              </View>
            </Layout.ItemMobile>
          )}
        />
      )}
      <ModalHalf
        testID="cms-filter-modal"
        title="Page Type"
        noTranslate={true}
        visible={showFilterModal}
        maxWidth={isWeb ? 450 : '100%'}
        compactPaddingBottom
        onClose={() => setShowFilterModal(false)}>
        <RadioButton
          testID="cms-filter-page-type"
          orientation="vertical"
          containerStyle={{ paddingHorizontal: 15, paddingVertical: 10 }}
          value={pageTypeFilter}
          data={PAGE_TYPE_FILTER_OPTIONS}
          onChange={(value) => onSelectPageTypeFilter(value)}
        />
      </ModalHalf>
    </Layout.Container>
  );
};

export default CMSScreen;
